import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Fab,
  Zoom,
  Slide,
  Divider,
  CircularProgress,
  Badge
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import SuggestedQuestions from './SuggestedQuestions';
import chatbotService from '../../services/chatbotService';

const ChatbotContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
}));

const ChatWindow = styled(Paper)(({ theme }) => ({
  width: 350,
  height: 500,
  maxWidth: '100vw',
  maxHeight: '80vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxShadow: theme.shadows[10],
  borderRadius: theme.spacing(2),
}));

const ChatHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const ChatBody = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2),
  overflowY: 'auto',
  backgroundColor: theme.palette.background.default,
}));

const WelcomeMessage = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[1],
}));

// Styled Badge for the chat icon
const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const location = useLocation();

  const toggleChat = async () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);

    if (newIsOpen) {
      // Reset unread count when opening chat
      setUnreadCount(0);

      // Start a new session if opening the chat
      if (!sessionId) {
        try {
          const newSessionId = await chatbotService.createSession();
          setSessionId(newSessionId);

          // Add welcome message when opening chat for the first time
          if (messages.length === 0) {
            const welcomeMessage = {
              id: `msg_${Date.now()}`,
              text: "Hello! I'm Laprecio's AI assistant. How can I help you today? You can ask me about our platform, registration process, trading, or compliance requirements.",
              timestamp: new Date().toISOString(),
              isBot: true
            };

            setMessages([welcomeMessage]);
          }
        } catch (error) {
          console.error('Error creating session:', error);
          // Create a local session ID as fallback
          setSessionId(`local_${Date.now()}`);

          // Add welcome message
          if (messages.length === 0) {
            const welcomeMessage = {
              id: `msg_${Date.now()}`,
              text: "Hello! I'm Laprecio's AI assistant. How can I help you today? You can ask me about our platform, registration process, trading, or compliance requirements.",
              timestamp: new Date().toISOString(),
              isBot: true
            };

            setMessages([welcomeMessage]);
          }
        }
      }
    }
  };

  const handleSendMessage = async (text) => {
    // Hide suggestions after first user message
    setShowSuggestions(false);

    // Make sure we have a session
    if (!sessionId) {
      try {
        const newSessionId = await chatbotService.createSession();
        setSessionId(newSessionId);
      } catch (error) {
        console.error('Error creating session:', error);
        // Create a local session ID as fallback
        setSessionId(`local_${Date.now()}`);
      }
    }

    // Add user message
    const userMessage = {
      id: `user_${Date.now()}`,
      text,
      timestamp: new Date().toISOString(),
      isUser: true
    };

    setMessages(prevMessages => [...prevMessages, userMessage]);
    setIsTyping(true);

    try {
      // Send message to the chatbot API
      const response = await chatbotService.sendMessage(sessionId, text);
      const botMessageId = `bot_${Date.now()}`;

      // Add bot response to messages
      setMessages(prevMessages => [
        ...prevMessages,
        {
          id: botMessageId,
          text: response.text,
          timestamp: response.timestamp || new Date().toISOString(),
          isBot: true,
          isError: response.isError || false
        }
      ]);

      // Increment unread count if chat is closed
      if (!isOpen) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error sending message:', error);

      // Add error message
      setMessages(prevMessages => [
        ...prevMessages,
        {
          id: `error_${Date.now()}`,
          text: "I'm having trouble connecting to the server. Please try again later or contact hello@laprecio.com for assistance.",
          timestamp: new Date().toISOString(),
          isBot: true,
          isError: true
        }
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  // Handle feedback from the user
  const handleFeedback = async (messageId, isPositive, feedbackText) => {
    try {
      // Find the message index in the conversation
      const messageIndex = messages.findIndex(msg => msg.id === messageId);
      if (messageIndex !== -1) {
        await chatbotService.submitFeedback(sessionId, messageIndex, isPositive, feedbackText);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  // Handle suggested question selection
  const handleSuggestedQuestion = (question) => {
    handleSendMessage(question);
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset chat when location changes
  useEffect(() => {
    setMessages([]);
    setSessionId(null);
    setIsOpen(false);
    setShowSuggestions(true);
  }, [location.pathname]);

  return (
    <ChatbotContainer>
      <Zoom in={!isOpen}>
        <Fab
          color="primary"
          aria-label="chat"
          onClick={toggleChat}
          sx={{ boxShadow: 3 }}
        >
          <StyledBadge badgeContent={unreadCount} color="secondary" invisible={unreadCount === 0}>
            <ChatIcon />
          </StyledBadge>
        </Fab>
      </Zoom>

      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <ChatWindow>
          <ChatHeader>
            <Typography variant="h6">Laprecio Assistant</Typography>
            <IconButton
              size="small"
              onClick={toggleChat}
              sx={{ color: 'white' }}
            >
              <CloseIcon />
            </IconButton>
          </ChatHeader>

          <Divider />

          <ChatBody>
            {messages.length === 0 ? (
              <WelcomeMessage>
                <Typography variant="body1" gutterBottom>
                  Welcome to Laprecio's AI assistant!
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Ask me anything about our platform, registration, trading, or compliance.
                </Typography>
              </WelcomeMessage>
            ) : (
              <>
                {showSuggestions && messages.length === 1 && (
                  <SuggestedQuestions onSelectQuestion={handleSuggestedQuestion} />
                )}

                {messages.map((message, index) => (
                  <ChatMessage
                    key={message.id || index}
                    message={message}
                    isUser={message.isUser}
                    onFeedback={handleFeedback}
                  />
                ))}
              </>
            )}

            {isTyping && (
              <Box display="flex" alignItems="center" ml={1} mt={2}>
                <CircularProgress size={20} thickness={5} />
                <Typography variant="body2" color="textSecondary" ml={1}>
                  Typing...
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </ChatBody>

          <ChatInput
            onSendMessage={handleSendMessage}
            disabled={isTyping}
          />
        </ChatWindow>
      </Slide>
    </ChatbotContainer>
  );
};

export default Chatbot;
